import React from 'react';
import PropTypes from 'prop-types';

import icWarningAlert from 'core/assets/svg/ic-warning-alert.svg';
import BaseDialog from 'core/components/base-dialog';
import { Button } from 'core/components/button';
import { Label } from 'core/components/label';
import { shortLaymanDateTimeFormatter } from 'core/helpers/formatters';
import { DRAFT_STATUS, DRAFT_UNAVAILABLE_REASON } from 'core/helpers/policy-drafting';
import useStyles from './policy-draft-unavailable-modal.styles';

const BLOCKING_DRAFT_COPY = {
  [DRAFT_STATUS.IN_REVIEW]: { titlePhrase: 'another draft in review', draftName: 'in-review draft' },
  [DRAFT_STATUS.CHANGES_REQUESTED]: {
    titlePhrase: 'another draft with changes requested',
    draftName: 'draft with changes requested'
  },
  [DRAFT_STATUS.APPROVED]: { titlePhrase: 'another approved draft', draftName: 'approved draft' }
};

const getBlockedContent = (blockingDraftStatus) => {
  const { titlePhrase, draftName } = BLOCKING_DRAFT_COPY[blockingDraftStatus];
  return {
    title: `You can’t edit this draft because the policy has ${titlePhrase}`,
    body: `You’re unable to work on any other drafts at this time. If the ${draftName} is issued, this draft you’re accessing will become invalid.`
  };
};

const CONTENT = {
  [DRAFT_UNAVAILABLE_REASON.OUTDATED]: {
    title: 'This draft is no longer valid',
    body: 'The policy has had an endorsement between the time the draft was created and now.'
  },
  [DRAFT_UNAVAILABLE_REASON.DELETED]: {
    title: 'This draft has been deleted',
    body: 'The draft you’re accessing has been deleted by a user.'
  },
  [DRAFT_UNAVAILABLE_REASON.NEWER_VERSION]: {
    title: 'This draft has a newer version',
    body: 'You’re viewing an older version of this draft, so it can’t be changed. Open the latest version to keep working on it.'
  }
};

const PolicyDraftUnavailableModal = ({
  reason,
  blockingDraftStatus,
  deletedBy,
  deletedAt,
  onClose,
  onViewOfficialPolicy,
  onOpenLatestVersion
}) => {
  const classes = useStyles();
  const { title, body } =
    reason === DRAFT_UNAVAILABLE_REASON.BLOCKED_BY_OTHER_DRAFT
      ? getBlockedContent(blockingDraftStatus)
      : CONTENT[reason];

  return (
    <BaseDialog size="sm" open onClose={onClose} className={classes.dialog}>
      <img src={icWarningAlert} alt="" className={classes.icon} />
      <Label type="h1" className={classes.title}>
        {title}
      </Label>
      <Label type="body1" className={classes.body}>
        {body}
      </Label>
      {reason === DRAFT_UNAVAILABLE_REASON.DELETED && (
        <div className={classes.details}>
          <Label type="body1Bold" className={classes.detailsTitle}>
            Technical details
          </Label>
          <div className={classes.detailsRow}>
            <Label type="body2">Action</Label>
            <Label type="body2Bold">Deleted</Label>
          </div>
          <div className={classes.detailsRow}>
            <Label type="body2">User</Label>
            <Label type="body2Bold">{deletedBy}</Label>
          </div>
          <div className={classes.detailsRow}>
            <Label type="body2">Time of action</Label>
            <Label type="body2Bold">{shortLaymanDateTimeFormatter(deletedAt)}</Label>
          </div>
        </div>
      )}
      <div className={classes.actions}>
        {reason === DRAFT_UNAVAILABLE_REASON.NEWER_VERSION ? (
          <Button variant="contained" color="primary" onClick={onOpenLatestVersion} className={classes.actionButton}>
            Open latest version
          </Button>
        ) : (
          <Button variant="contained" color="primary" onClick={onViewOfficialPolicy} className={classes.actionButton}>
            View official policy
          </Button>
        )}
      </div>
    </BaseDialog>
  );
};

PolicyDraftUnavailableModal.propTypes = {
  reason: PropTypes.oneOf(Object.values(DRAFT_UNAVAILABLE_REASON)).isRequired,
  blockingDraftStatus: PropTypes.oneOf(Object.keys(BLOCKING_DRAFT_COPY)),
  deletedBy: PropTypes.string,
  deletedAt: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onViewOfficialPolicy: PropTypes.func.isRequired,
  onOpenLatestVersion: PropTypes.func.isRequired
};

PolicyDraftUnavailableModal.defaultProps = {
  blockingDraftStatus: null,
  deletedBy: null,
  deletedAt: null
};

export default PolicyDraftUnavailableModal;
